import { client } from '@/api/http';
import AxiosBrowserCacheUrlMutation from '@/api/AxiosBrowserCacheUrlMutation';
import { type AxiosRequestConfig, HttpStatusCode } from 'axios';
import DownloadUtil from '@/api/DownloadUtil';
import { taggedLogger } from '@/util';

const log = taggedLogger('load-image');

/**
 * Fetch an image as a Blob. Shared HTTP fetch logic used by both the data URL
 * and ImageBitmap loading paths.
 */
export async function loadImageAsBlob(
    fileUrl: string,
    config?: AxiosRequestConfig,
): Promise<Blob> {
    const response = await client.get<ArrayBuffer>(
        fileUrl,
        AxiosBrowserCacheUrlMutation.makeMutationOption({
            headers: {
                // Let the service decide the best image representation
                accept: 'image/*;q=0.5',
            },
            // arraybuffer, blob, stream, text, json, document
            responseType: 'arraybuffer',
            // Disable response transformation for this request. Models should be transferred without
            // any mutation or modification.
            transformResponse: (data /*: any, headers?: any */) => {
                return data;
            },
            ...config,
        }),
    );
    if (response.status === HttpStatusCode.Ok && response.data) {
        const blob = new Blob([response.data], {
            type: DownloadUtil.getContentType(response.headers, 'image/jpeg'),
        });
        log.debug('Loaded image blob of %d bytes of %s', blob.size, blob.type);
        return blob;
    }
    throw new Error(`Failed to get image blob for '${fileUrl}'. Code '${response.status}'`);
}

export async function loadImageAsUrl(
    fileUrl: string,
    config?: AxiosRequestConfig,
): Promise<string | undefined> {
    const blob = await loadImageAsBlob(fileUrl, config);
    return _readBlobToDataUrl(blob);
}

function _readBlobToDataUrl(blob: Blob): Promise<string> {
    return new Promise<string>((resolve, reject) => {
        try {
            const reader = new FileReader();
            reader.onerror = (ev: ProgressEvent<FileReader>) => {
                if (ev.target) {
                    ev.target.abort();
                    reject(ev.target.error);
                } else {
                    reject(new Error(`Known error loading image from blob`));
                }
            };
            reader.onload = (ev: ProgressEvent<FileReader>) => {
                if (ev.target) {
                    resolve(reader.result as string);
                } else {
                    reject(new Error('FileReader onload fired without a target'));
                }
            };
            reader.readAsDataURL(blob);
        } catch (err) {
            reject(err);
        }
    });
}
